import {
    InfoIcon,
    Typography,
    WithLoader,
} from '@analytical-alley/ui';

import { ComparisonChartCard } from '@/dashboards/ExecutiveSummary/components/ComparisonChartCard';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import { formatRangeLabel } from '@/dashboards/ExecutiveSummary/helpers/formatRangeLabel';
import {
    useGeneralSummarySpendQuery,
    useTotalKpiQuery,
} from '@/dashboards/ExecutiveSummary/hooks';
import { useConfigContext } from '@/context/configContext';
import React, { useMemo } from 'react';
import { getROIConfig } from '@/dashboards/SpendSummary/utils';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

export const GeneralSummaryCharts = () => {
    const { data, isLoading, isFetching } = useGeneralSummarySpendQuery();
    const {
        data: totalMediaKpi,
        isLoading: isTotalKpiLoading,
        isFetching: isTotalKpiFetching,
    } = useTotalKpiQuery();
    const {
        project: { roiType },
        contributionFormatter,
    } = useDashboardGlobalContext();
    const { formatNumber } = useConfigContext();
    const { primaryRange, comparisonRange } = useExecutiveSummaryContext();

    const { name, calculate, description } = useMemo(
        () => getROIConfig(roiType),
        [roiType],
    );

    const primaryLabel = formatRangeLabel(primaryRange);
    const comparisonLabel = comparisonRange
        ? formatRangeLabel(comparisonRange)
        : undefined;

    const primaryTotalRoi = calculate({
        spend: data.roiPrimary.totalSpend,
        contribution: data.roiPrimary.totalContribution,
        contributionCostProduct: data.roiPrimary.totalContributionProduct,
    });

    const comparisonTotalRoi = calculate({
        spend: data.roiComparison.totalSpend,
        contribution: data.roiComparison.totalContribution,
        contributionCostProduct: data.roiComparison.totalContributionProduct,
    });

    return (
        <div className="glass-tile flex flex-col gap-2 px-8 py-6 max-lg:px-5 max-lg:max-w-full">
            <Typography variant="titleS">General Summary</Typography>
            <WithLoader
                isLoading={isLoading || isTotalKpiLoading}
                isFetching={isFetching || isTotalKpiFetching}
            >
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <ComparisonChartCard
                        title="Total KPI"
                        axisLabel="Total KPI"
                        primary={totalMediaKpi.primary}
                        comparison={totalMediaKpi.comparison}
                        formatValue={contributionFormatter}
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                    <ComparisonChartCard
                        title="Total Media Spend"
                        axisLabel="Media Spend"
                        primary={data.primary.totalSpend}
                        comparison={data.comparison.totalSpend}
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                    <ComparisonChartCard
                        title="Total Media Contribution"
                        axisLabel="Media Contribution"
                        primary={data.primary.totalContribution}
                        comparison={data.comparison.totalContribution}
                        formatValue={contributionFormatter}
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                    <ComparisonChartCard
                        title={
                            <span className="flex justify-center gap-1 font-medium">
                                Total Media {name}
                                <InfoIcon>{description}</InfoIcon>
                            </span>
                        }
                        axisLabel={`Media ${name}`}
                        primary={primaryTotalRoi}
                        comparison={comparisonTotalRoi}
                        formatValue={(v) =>
                            roiType === 'COST_PER_UNIT'
                                ? formatNumber(v, {
                                      style: 'currency',
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                  })
                                : v.toFixed(2)
                        }
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                </div>
            </WithLoader>
        </div>
    );
};
